import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Logo } from "../shared/Logo";
import { Clock } from "../shared/Clock";
import { ROLE_COLORS, ROLE_LABELS } from "../shared/fmt";

interface NavItem {
	id: string;
	icon: string;
	label: string;
	roles: string[];
}
const NAV: { section: string; items: NavItem[] }[] = [
	{
		section: "Overview",
		items: [
			{
				id: "dashboard",
				icon: "📊",
				label: "Dashboard",
				roles: ["admin", "manager", "cashier"],
			},
		],
	},
	{
		section: "POS",
		items: [{ id: "pos", icon: "🖥️", label: "Open POS", roles: ["cashier"] }],
	},
	{
		section: "Restaurant",
		items: [
			{
				id: "orders",
				icon: "📋",
				label: "Orders",
				roles: ["admin", "manager", "cashier"],
			},
			{
				id: "tables",
				icon: "🪑",
				label: "Tables",
				roles: ["admin", "manager", "cashier"],
			},
		],
	},
	{
		section: "Menu",
		items: [
			{
				id: "menu",
				icon: "🍽️",
				label: "Menu Items",
				roles: ["admin", "manager"],
			},
			{
				id: "categories",
				icon: "🗂️",
				label: "Categories",
				roles: ["admin", "manager"],
			},
			{
				id: "deals",
				icon: "🏷️",
				label: "Deals & Promos",
				roles: ["admin", "manager", "cashier"],
			},
		],
	},
	{
		section: "Management",
		items: [
			{ id: "users", icon: "👥", label: "Users & Roles", roles: ["admin"] },
			{
				id: "payments",
				icon: "💳",
				label: "Payments",
				roles: ["admin", "manager"],
			},
		],
	},
	{
		section: "Reports",
		items: [
			{
				id: "reports",
				icon: "📈",
				label: "Sales Reports",
				roles: ["admin", "manager"],
			},
			{
				id: "financial",
				icon: "💰",
				label: "Financial Reports",
				roles: ["admin", "manager"],
			},
			{
				id: "inventory",
				icon: "📦",
				label: "Inventory",
				roles: ["admin", "manager"],
			},
		],
	},
	{
		section: "System",
		items: [
			{
				id: "settings",
				icon: "⚙️",
				label: "Settings",
				roles: ["admin", "manager"],
			},
		],
	},
];

export const AdminLayout: React.FC<{
	children: React.ReactNode;
	title: string;
}> = ({ children, title }) => {
	const { user, logout } = useAuth();
	if (!user) return null;
	const rc = ROLE_COLORS[user.role] || "#888";

	return (
		<div className="admin-layout">
			{/* Sidebar */}
			<div className="sidebar">
				<div className="sb-header">
					<Logo />
					{/* <div>
						<div className="sb-brand">Teknuluguy</div>
						<div className="sb-sublabel">Admin Panel</div>
					</div> */}
				</div>

				<nav className="sb-nav">
					{NAV.map((sec) => {
						const vis = sec.items.filter((n) => n.roles.includes(user.role));
						if (!vis.length) return null;
						return (
							<div key={sec.section}>
								<div className="sb-section">{sec.section}</div>
								{vis.map((n) => (
									<NavLink
										key={n.id}
										to={`/${n.id}`}
										className={({ isActive }) =>
											`nav-btn${isActive ? " active" : ""}`
										}>
										<span className="nav-icon">{n.icon}</span>
										<span>{n.label}</span>
									</NavLink>
								))}
							</div>
						);
					})}
				</nav>

				<div className="sb-footer">
					<div className="user-pill" onClick={logout} title="Click to logout">
						<div className="user-avatar" style={{ background: rc }}>
							{user.avatar}
						</div>
						<div>
							<div className="user-name">
								{user.name.split(" ").slice(0, 2).join(" ")}
							</div>
							<div className="user-role">{ROLE_LABELS[user.role]} · Logout</div>
						</div>
					</div>
				</div>
			</div>

			{/* Main */}
			<div className="main-area">
				<div className="topbar">
					<div className="topbar-title">{title}</div>
					<div className="topbar-right">
						<div className="chip green">
							<div className="chip-dot" style={{ background: "#06a384" }} />
							System Online
						</div>
						<div className="chip">
							{new Date().toLocaleDateString("en-KW", {
								day: "numeric",
								month: "short",
								year: "numeric",
							})}
						</div>
						<Clock />
					</div>
				</div>
				<div className="page-content">{children}</div>
			</div>
		</div>
	);
};
